import React, { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import ForgetPassword from "../pages/common/ForgetPassword";
import Login from "../pages/common/Login";
import SignUp from "../pages/common/SignUp";
import OTPVerification from "../pages/common/OTPVerification";
import ResetPassword from "../pages/common/ResetPassword";
import AccountSetup from "../pages/common/AccountSetup";
import MerchantOnboardLayout from "../components/Merchant/MerchantOnboarding/MerchantOnboardLayout";
import Privacy from "../components/common/Privacy";
import { useSelector, useDispatch } from "react-redux";
import { getOnboardingStatus } from "../services/fundraiserService";
import { clearAuth } from "../redux/slices/authSlice";
import Loader from "../components/common/Loader";
import axios from "../utils/axios";

// Component to handle fundraiser redirect with Stripe check
const FundraiserRedirect = () => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state?.authReducer?.tokenData?.token);
  const [isChecking, setIsChecking] = useState(true);
  const [redirectPath, setRedirectPath] = useState(null);

  useEffect(() => {
    const checkStripeAndRedirect = async () => {
      // If no token, redirect to login
      if (!token) {
        setRedirectPath("/fundraiser/account-login");
        setIsChecking(false);
        return;
      }

      try {
        const statusPromise = await dispatch(getOnboardingStatus()).unwrap();
        const detailsSubmitted = statusPromise?.data?.detailsSubmitted === true;
        const chargesEnabled = statusPromise?.data?.chargesEnabled === true;

        if (detailsSubmitted && chargesEnabled) {
          // Set the flag to indicate Stripe completion
          sessionStorage.setItem('fundraiser_stripe_complete', 'true');
          
          // Stripe complete - check approval status before redirecting to dashboard
          try {
            const checkResponse = await axios.get("fundraiser/profile", {
              headers: { 
                Authorization: `Bearer ${token}`,
                "api-key": process.env.REACT_APP_API_KEY
              }
            });
            
            const fundraiserProfile = checkResponse?.data?.data;
            const profileStatus = typeof fundraiserProfile?.status === "string" 
              ? fundraiserProfile.status.toLowerCase() 
              : fundraiserProfile?.status;
            const isApproved = profileStatus === "approved";

            if (isApproved) {
              // Approved AND Stripe complete - go to dashboard
              setRedirectPath("/fundraiser/dashboard");
            } else {
              // Stripe complete but NOT approved - clear auth and redirect to login with modal
              dispatch(clearAuth());
              sessionStorage.setItem('showAccountPendingModal', 'true');
              setRedirectPath("/fundraiser/account-login");
            }
          } catch (profileError) {
            console.error("Failed to fetch fundraiser profile:", profileError);
            // If profile fetch fails, clear auth and redirect to login
            dispatch(clearAuth());
            setRedirectPath("/fundraiser/account-login");
          }
        } else if (detailsSubmitted && !chargesEnabled) {
          // Stripe partial - go to complete setup
          setRedirectPath("/complete-setup");
        } else {
          // No Stripe - go to welcome (Stripe connect flow)
          setRedirectPath("/fundraiser/welcome");
        }
      } catch (error) {
        // If Stripe check fails, default to welcome page
        setRedirectPath("/fundraiser/welcome");
      } finally {
        setIsChecking(false);
      }
    };

    checkStripeAndRedirect();
  }, [dispatch, token]);

  if (isChecking) {
    return <Loader />;
  }

  return redirectPath ? <Navigate to={redirectPath} replace /> : <Loader />;
};

const CommonRoutes = () => {
  const token = useSelector((state) => state?.authReducer?.tokenData?.token);
  const role = useSelector(
    (state) => state?.authReducer?.tokenData?.user?.role
  );

  const [isAuthenticated, setIsAuthenticated] = useState(!!token);

  const getRedirectPath = () => {
    switch (role) {
      case "merchant":
        return "/merchant/dashboard";
      // return "/merchant/welcome"; // Coming Soon screen commented out
      case "fundraiser":
        // For fundraisers, use FundraiserRedirect component to check Stripe first
        return null; // Will be handled by FundraiserRedirect
      default:
        return "/dashboard"; // Default dashboard
    }
  };

  useEffect(() => {
    setIsAuthenticated(!!token);
  }, [token]);

  return (
    <Routes>
      <Route
        path="/"
        element={
          !isAuthenticated ? (
            <Login />
          ) : role === "fundraiser" ? (
            <FundraiserRedirect />
          ) : (
            <Navigate to={getRedirectPath()} />
          )
        }
      />
      <Route path="/complete-setup" element={<AccountSetup />} />
      {/* <Route
        path="/signup"
        element={
          !isAuthenticated ? (
            <SignUp />
          ) : role === "fundraiser" ? (
            <FundraiserRedirect />
          ) : (
            <Navigate to={getRedirectPath()} />
          )
        }
      /> */}
      <Route path="/forget-password" element={<ForgetPassword />} />
      <Route path="/otp-verification" element={<OTPVerification />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/privacy-policy" element={<Privacy />} />
    </Routes>
  );
};

export default CommonRoutes;